import { useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'

import { digObject, sortArrayBy, toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import autoSelectProductOptions from '@functions/autoSelectProductOptions'
import autoSelectProductQuantityAndOptions from '@functions/autoSelectProductQuantityAndOptions'
import checkProductRequiredCampaignFields from '@functions/checkProductRequiredCampaignFields'
import filterPriceListItemsByPriceListAndProduct from '@functions/filterPriceListItemsByPriceListAndProduct'
import findCurrentAgencyPriceList from '@functions/findCurrentAgencyPriceList'
import selectPriceListItemOptionValue from '@functions/selectPriceListItemOptionValue'
import { buildOptionsString } from '@functions/orderItem'

import useAgencyOrderBundles from '@hooks/useAgencyOrderBundles'
import useAgencyPrice from '@hooks/useAgencyPrice'
import useAgencyProviderPackages from '@hooks/useAgencyProviderPackages'
import useAgencyProviders from '@hooks/useAgencyProviders'
import useCampaignQuoteItem from '@hooks/useCampaignQuoteItem'
import useCampaignQuoteTemplateItem from '@hooks/useCampaignQuoteTemplateItem'
import useDefaultQuoteItem from '@hooks/useDefaultQuoteItem'
import useFavouriteOrder from '@hooks/useFavouriteOrder'
import useFavouriteOrders from '@hooks/useFavouriteOrders'
import useOrder from '@hooks/useOrder'
import useProviderPriceListProductUnlocks from '@hooks/useProviderPriceListProductUnlocks'
import useProviderPriceLists from '@hooks/useProviderPriceLists'
import usePublicationEditions from '@hooks/usePublicationEditions'
import usePublications from '@hooks/usePublications'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type {
  CampaignQuoteItemModel,
  CampaignQuoteTemplateItemModel,
  CreateMultipleCampaignQuoteItemsPayload,
  CreateMultipleCampaignQuoteTemplateItemsPayload,
  CreateMultipleDefaultQuoteItemsPayload,
  DefaultQuoteItemModel,
  OrderModel,
  ProviderPriceListItemOptionModel,
} from '@models/types'

import autoSelectProviderId from '../functions/autoSelectProviderId'
import buildBundleOrder from '../functions/buildBundleOrder'
import buildFavouriteOrder from '../functions/buildFavouriteOrder'
import buildProviderPackageOrder from '../functions/buildProviderPackageOrder'
import buildPublicationOrder from '../functions/buildPublicationOrder'
import buildStandardOrder from '../functions/buildStandardOrder'
import filterCategoriesByService from '../functions/filterCategoriesByService'
import filterProductsByPriceListAndStatus from '../functions/filterProductsByPriceListAndStatus'
import filterProvidersWithPackages from '../functions/filterProvidersWithPackages'

import defaultRequestOptions from '../defaultRequestOptions'

const dateNow = DateTime.now()

export type ProductResult = {
  price_list_id: number,
  price_list_title: string,
  product_id: number,
  product_title: string,
  provider_id: number,
  provider_title: string,
  result_type: 'ServiceProduct',
  service_id: number,
  service_category_id?: number,
  service_title: string,
}

type DefaultState = {
  packageTitleFilter?: string,
  selectedCategoryId?: number,
  selectedLiftoverIds: number[],
  selectedProviderId?: number,
  selectedProviderPriceListItemId?: number,
  selectedProviderPriceListItemOptions?: object[],
  selectedPublicationEditionIds: number[],
  selectedPublicationId?: number,
  selectedServiceId: number | '' | 'bundles' | 'favourites' | 'packages',
  selectedServiceProductId?: number,
  selectedTags: string[],
}

const defaultState: DefaultState = {
  packageTitleFilter: undefined,
  selectedCategoryId: undefined,
  selectedLiftoverIds: [],
  selectedProviderId: undefined,
  selectedProviderPriceListItemId: undefined,
  selectedProviderPriceListItemOptions: undefined,
  selectedPublicationEditionIds: [],
  selectedPublicationId: undefined,
  selectedServiceId: '',
  selectedServiceProductId: undefined,
  selectedTags: [],
}

export type SetState = (state: Partial<typeof defaultState>) => void

type UseNewOrderOptions = {
  agencyId: number,
  campaignId: number,
  campaignProposalId?: number,
  campaignQuoteId?: number,
  campaignQuoteTemplateId?: number,
  existingOrderId?: number,
  isDefaultQuoteItem?: boolean,
  providerId?: number,
  quoteSectionId?: number,
  redirect?: string,
  serviceId?: number,
}

function useNewOrder(options: UseNewOrderOptions) {
  const {
    agencyId,
    campaignId,
    campaignProposalId,
    campaignQuoteId,
    campaignQuoteTemplateId,
    existingOrderId,
    isDefaultQuoteItem,
    providerId,
    quoteSectionId,
    redirect,
  } = options

  // State
  const [state, setState] = useSetState(defaultState)
  const {
    packageTitleFilter,
    selectedCategoryId,
    selectedLiftoverIds,
    selectedProviderId,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions,
    selectedPublicationEditionIds,
    selectedPublicationId,
    selectedServiceId,
    selectedServiceProductId,
    selectedTags,
  } = state

  // Entities
  const entities = useSelector(reduxState => reduxState.entities)
  const {
    _updates: {
      // @ts-expect-error TS2339
      serviceProducts: serviceProductsUpdatedAt,
    },
    agencies,
    agencyProviders,
    campaigns,
    favouriteOrders,
    orderBundles,
    providerPackages,
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
    providerPriceListItems,
    providerPriceLists,
    providers,
    publications,
    serviceCategories,
    serviceProducts,
    services,
  } = entities

  // Selected Entities
  const selectedAgency = agencies[agencyId] || {}
  const selectedCampaign = campaigns[campaignId] || {}
  // @ts-expect-error TS2538
  const selectedCategory = serviceCategories[selectedCategoryId] || {}
  // @ts-expect-error TS2538
  const selectedPriceListItem = providerPriceListItems[selectedProviderPriceListItemId] || {}
  // @ts-expect-error TS2538
  const selectedProvider = providers[selectedProviderId] || {}
  // @ts-expect-error TS2538
  const selectedPublication = publications[selectedPublicationId] || {}
  const selectedService = services[selectedServiceId] || {}
  // @ts-expect-error TS2538
  const selectedServiceProduct = serviceProducts[selectedServiceProductId] || {}
  const isPublication = digObject(selectedService, 'options.publication', false)

  const currentPriceList = findCurrentAgencyPriceList({
    agencyId,
    agencyProviders,
    // @ts-expect-error TS2322
    providerId: selectedProviderId,
    providerPriceLists,
    publication: selectedPublication,
    serviceId: selectedService.id,
    startDateString: selectedCampaign?.dates?.start?.date,
  })

  const filteredCategories = useMemo(() => (
    filterCategoriesByService({
      service: selectedService,
      serviceCategories,
    })
  ), [selectedService.id])
  const hasFilteredCategories = !!filteredCategories.length

  const filteredServiceProducts = useMemo(() => (
    filterProductsByPriceListAndStatus({
      agency: selectedAgency,
      // @ts-expect-error TS2740
      providerPriceList: currentPriceList,
      serviceCategory: selectedCategory,
      serviceProducts,
    })
  // @ts-expect-error TS2339
  ), [selectedAgency.id, selectedCategory.id, currentPriceList.id, serviceProductsUpdatedAt])

  const filteredProviderPriceListItems = useMemo(() => (
    filterPriceListItemsByPriceListAndProduct({
      providerPriceList: currentPriceList,
      providerPriceListItems,
      serviceProduct: selectedServiceProduct,
    })
  // @ts-expect-error TS2339
  ), [currentPriceList.id, selectedServiceProduct.id])

  const { missingFields: missingCampaignFields } = checkProductRequiredCampaignFields({
    campaign: selectedCampaign,
    serviceProduct: selectedServiceProduct,
  })

  // Load Agency Providers, Services and Providers
  const agencyProvidersPayload = useAgencyProviders({
    filters: {
      agency_id: agencyId,
      provider_id: providerId,
      service_id: selectedService.id,
    },
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.agencyProvider,
    sortKey: 'provider_title',
  })

  const {
    filteredAgencyProviders,
    loading: loadingProviders,
  } = agencyProvidersPayload

  // Providers
  const assignedProviderIds = filteredAgencyProviders.map(agencyProvider => agencyProvider.provider_id)
  // @ts-expect-error TS2802
  const uniqueProviderIds = [...new Set(assignedProviderIds)]
  const filteredProviders = uniqueProviderIds.map(id => providers[id]).filter(provider => provider?.active)

  // Services
  const assignedServiceIds = filteredAgencyProviders.map(agencyProvider => agencyProvider.service_id)
  // @ts-expect-error TS2802
  const uniqueServiceIds = [...new Set(assignedServiceIds)]
  const uniqueServices = uniqueServiceIds.map(id => services[id]).filter(service => service)
  const filteredServices = sortArrayBy(uniqueServices, 'asc', 'title')

  // Load Agency Order Bundles
  const agencyOrderBundlesPayload = useAgencyOrderBundles({
    filters: {
      agency_id: selectedAgency.id,
      hidden: 'false',
    },
    performHttpRequests: selectedServiceId === 'bundles',
    requestOptions: {
      ...defaultRequestOptions.orderBundle,
      ...defaultRequestOptions.orderBundleItem,
      include_agency_order_bundle_order_bundle: true,
    },
  })

  const { filteredAgencyOrderBundles } = agencyOrderBundlesPayload

  // Load Favourite Orders
  const favouriteOrdersPayload = useFavouriteOrders({
    filters: {
      agency_id: selectedAgency.id,
    },
    performHttpRequests: selectedServiceId === 'favourites',
    requestOptions: defaultRequestOptions.favouriteOrder,
  })

  const { filteredFavouriteOrders } = favouriteOrdersPayload

  // Load Agency Provider Packages
  const agencyProviderPackagesPayload = useAgencyProviderPackages({
    filters: {
      agency_id: selectedAgency.id,
      hidden: 'false',
      package_title: packageTitleFilter,
      provider_id: selectedProviderId,
    },
    performHttpRequests: selectedServiceId === 'packages',
    requestOptions: {
      ...defaultRequestOptions.providerPackage,
      include_agency_provider_package_provider_package: true,
    },
  })

  const { filteredAgencyProviderPackages } = agencyProviderPackagesPayload

  const filteredProviderPackages = filteredAgencyProviderPackages.map(agencyProviderPackage => (
    providerPackages[agencyProviderPackage.provider_package_id]
  )).filter(p => p)

  // Providers with Packages
  const { providersWithPackages: filteredProvidersWithPackages } = filterProvidersWithPackages({
    providers,
    providerPackages: filteredProviderPackages,
  })

  // Load Product Unlocks
  const providerPriceListProductUnlocksPayload = useProviderPriceListProductUnlocks({
    filters: {
      // @ts-expect-error TS2339
      provider_price_list_id: currentPriceList.id,
      service_product_id: selectedServiceProductId,
    },
    // @ts-expect-error TS2339
    performHttpRequests: isPublication && !!currentPriceList.id && selectedServiceProductId,
    requestOptions: defaultRequestOptions.providerPriceListProductUnlock,
  })

  const { filteredProviderPriceListProductUnlocks } = providerPriceListProductUnlocksPayload

  // Load Provider Pricelists
  const providerPriceListsPayload = useProviderPriceLists({
    filters: {
      // publication price lists arent scoped to agency at this stage
      // so remove the param
      agency_id: selectedPublicationId ? undefined : agencyId,
      provider_id: selectedProviderId,
      publication_id: selectedPublicationId,
      // @ts-expect-error TS2322
      service_id: selectedServiceId,
      start_date: selectedCampaign?.start_date,
    },
    performHttpRequests: (
      !!agencyId
      && !!selectedProviderId
      && (!isPublication || !!selectedPublicationId)
      && !!selectedServiceId
    ),
    requestOptions: defaultRequestOptions.providerPriceList,
  })

  const { loading: loadingPriceLists } = providerPriceListsPayload

  // Load Publications
  const publicationsPayload = usePublications({
    performHttpRequests: isPublication,
    requestOptions: defaultRequestOptions.publication,
  })
  const { filteredPublications, loading: loadingPublications } = publicationsPayload

  // Load Publication Editions
  const publicationEditionsPayload = usePublicationEditions({
    filters: {
      min_publication_date: dateNow.toFormat('yyyy-MM-dd'),
      publication_id: selectedPublicationId,
    },
    performHttpRequests: isPublication && !!selectedPublicationId,
    requestOptions: defaultRequestOptions.publicationEdition,
  })
  const { filteredPublicationEditions, loading: loadingPublicationEditions } = publicationEditionsPayload

  // Load Agency Price
  const { currentPrice, loading: loadingAgencyPrice } = useAgencyPrice({
    agency: selectedAgency,
    campaign: selectedCampaign,
    performHttpRequests: !missingCampaignFields.length && selectedProviderPriceListItemOptions !== undefined,
    providerPriceListItem: selectedPriceListItem,
    // @ts-expect-error TS2322
    selectedProviderPriceListItemOptions,
  })

  // Load API Pricing
  const { data: apiPricingItem, loading: loadingApiPricing } = useReduxAction(
    'providerPriceListItems',
    'buildProviderPriceListItemOptions',
    {
      agency_id: selectedAgency.id,
      campaign_id: selectedCampaign?.id,
      entityId: selectedPriceListItem?.id,
    },
    [selectedPriceListItem?.id, missingCampaignFields.length],
    {
      dispatchAction: (action, requestOptions) => action(selectedPriceListItem.id, requestOptions),
      shouldClearResult: !selectedServiceProductId,
      shouldPerformFn: () => (
        selectedPriceListItem?.api_pricing && !missingCampaignFields.length
      ),
      skipLoadedForKeysCheck: true,
    },
  )

  // Campaign Quote Item
  const campaignQuoteItemPayload = useCampaignQuoteItem({ id: undefined })
  const {
    callbacks: {
      createCampaignQuoteItem,
      createCampaignQuoteItems,
    },
    creating: creatingCampaignQuoteItem,
  } = campaignQuoteItemPayload

  // Campaign Quote Template Item
  const campaignQuoteTemplateItemPayload = useCampaignQuoteTemplateItem({ id: undefined })
  const {
    callbacks: {
      createCampaignQuoteTemplateItem,
      createCampaignQuoteTemplateItems,
    },
    creating: creatingCampaignQuoteTemplateItem,
  } = campaignQuoteTemplateItemPayload

  const defaultQuoteItemPayload = useDefaultQuoteItem({ owner_id: agencyId, owner_type: 'Agency' })
  const {
    callbacks: {
      createDefaultQuoteItem,
      createDefaultQuoteItems,
      createOrEditDefaultQuoteItem,
    },
    creating: creatingDefaultQuoteItem,
  } = defaultQuoteItemPayload

  const orderPayload = useOrder({ agency_id: agencyId })
  const {
    callbacks: {
      addItemToOrder,
      createOrder,
      createOrderWithOrderItem,
      createOrders,
    },
    creating: creatingOrder,
  } = orderPayload

  const favouriteOrderPayload = useFavouriteOrder({ agency_id: agencyId })
  const {
    callbacks: { createOrderFromFavourite },
    creating: creatingFavouriteOrder,
  } = favouriteOrderPayload

  // Auto Select Qty & Options
  useEffect(() => {
    // @ts-expect-error TS2339
    if (currentPriceList.id && selectedServiceProductId){
      const autoSelectPayload = autoSelectProductQuantityAndOptions({
        providerPriceList: currentPriceList,
        providerPriceListItemOptionValues,
        providerPriceListItemOptions,
        providerPriceListItems,
        serviceProduct: selectedServiceProduct,
      })

      setState({ selectedProviderPriceListItemId: autoSelectPayload.firstPriceListItem?.id })

      if (!autoSelectPayload.firstPriceListItem?.api_pricing){
        setState({ selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions })
      }
    }
  // @ts-expect-error TS2339
  }, [currentPriceList.id, selectedServiceProductId])

  // Autoselect Options when using API Pricing
  useEffect(() => {
    if (
      // @ts-expect-error TS2339
      apiPricingItem?.api_pricing
      && !loadingApiPricing
      && selectedPriceListItem?.id
      && !selectedProviderPriceListItemOptions?.length
    ){
      const autoSelectPayload = autoSelectProductOptions({
        // @ts-expect-error TS2740
        providerPriceListItem: apiPricingItem,
        providerPriceListItemOptionValues,
        providerPriceListItemOptions,
      })

      setState({
        // @ts-expect-error TS2339
        selectedProviderPriceListItemId: apiPricingItem.id,
        selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
      })
    }
  // @ts-expect-error TS2339
  }, [apiPricingItem?.id, loadingApiPricing, selectedPriceListItem?.id])

  // Options String
  // @ts-expect-error TS2345
  const selectedOptionsString = buildOptionsString(selectedProviderPriceListItemOptions || [])

  // Valid Order
  const validOrder = !!selectedServiceProduct.id
    && !missingCampaignFields.length
    && !!selectedPriceListItem.id

  return {
    callbacks: {
      addItemToOrder: (params: object) => (
        // @ts-expect-error TS2345
        addItemToOrder(existingOrderId, params, { ...defaultRequestOptions.order })
      ),
      buildFavouriteOrder: (params: { favouriteOrderId: number }) => buildFavouriteOrder({
        campaignId,
        campaignQuoteId,
        campaignQuoteTemplateId,
        favouriteOrderId: params.favouriteOrderId,
        favouriteOrders,
        quoteSectionId,
        redirect,
      }),
      buildBundleOrder: (params: { orderBundleId: number }) => buildBundleOrder({
        campaignId,
        campaignQuoteId,
        campaignQuoteTemplateId,
        orderBundleId: params.orderBundleId,
        orderBundles,
        quoteSectionId,
        redirect,
      }),
      buildProviderPackageOrder: (params: { providerPackageId: number }) => buildProviderPackageOrder({
        agencyId,
        campaignId,
        campaignQuoteId,
        campaignQuoteTemplateId,
        isDefaultQuoteItem,
        providerPackageId: params.providerPackageId,
        providerPackages,
        quoteSectionId,
        redirect,
      }),
      buildPublicationOrder: () => buildPublicationOrder({
        campaignId,
        campaignQuoteId,
        campaignQuoteTemplateId,
        quoteSectionId,
        redirect,
        selectedLiftoverIds,
        selectedProviderPriceListItemId,
        // @ts-expect-error TS2322
        selectedProviderPriceListItemOptions,
        selectedPublicationEditionIds,
        selectedPublicationId,
        selectedServiceProductId,
        selectedTags,
        serviceProducts,
      }),
      buildStandardOrder: () => buildStandardOrder({
        agencyId,
        campaignId,
        campaignProposalId,
        campaignQuoteId,
        campaignQuoteTemplateId,
        existingOrderId,
        isDefaultQuoteItem,
        quoteSectionId,
        redirect,
        selectedProviderPriceListItemId,
        // @ts-expect-error TS2322
        selectedProviderPriceListItemOptions,
        selectedServiceProductId,
        selectedTags,
        serviceProducts,
      }),
      createCampaignQuoteItem: (params: Partial<CampaignQuoteItemModel>) => (
        createCampaignQuoteItem(params, {
          ...defaultRequestOptions.campaignQuoteItem,
        })
      ),
      createCampaignQuoteItems: (
        orderType: 'bundle' | 'publication',
        params: CreateMultipleCampaignQuoteItemsPayload,
      ) => (
        createCampaignQuoteItems(orderType, params, {
          ...defaultRequestOptions.campaignQuoteItem,
        })
      ),
      createCampaignQuoteTemplateItem: (params: Partial<CampaignQuoteTemplateItemModel>) => (
        createCampaignQuoteTemplateItem(params, {
          ...defaultRequestOptions.campaignQuoteTemplateItem,
        })
      ),
      createCampaignQuoteTemplateItems: (
        orderType: 'bundle' | 'publication',
        params: CreateMultipleCampaignQuoteTemplateItemsPayload,
      ) => (
        createCampaignQuoteTemplateItems(orderType, params, {
          ...defaultRequestOptions.campaignQuoteTemplateItem,
        })
      ),
      createDefaultQuoteItem: (params: Partial<DefaultQuoteItemModel>) => (
        createDefaultQuoteItem(params, { ...defaultRequestOptions.defaultQuoteItem })
      ),
      createDefaultQuoteItems: (
        params: CreateMultipleDefaultQuoteItemsPayload,
      ) => (
        // @ts-expect-error TS2304
        createDefaultQuoteItems(orderType, params, {
          ...defaultRequestOptions.campaignQuoteTemplateItem,
        })
      ),
      createOrder: (params: Partial<OrderModel>) => (
        createOrder(params, { ...defaultRequestOptions.order })
      ),
      createOrEditDefaultQuoteItem: (params: Partial<DefaultQuoteItemModel>) => (
        // @ts-expect-error TS2345
        createOrEditDefaultQuoteItem(params)
      ),
      createOrderFromFavourite: (params: object) => (
        // @ts-expect-error TS2345
        createOrderFromFavourite(params, { ...defaultRequestOptions.order })
      ),
      createOrderWithOrderItem: (params: object) => (
        // @ts-expect-error TS2345
        createOrderWithOrderItem(params, { ...defaultRequestOptions.order })
      ),
      createOrders: (params: object) => (
        createOrders(params, { ...defaultRequestOptions.order })
      ),
      resetState: () => setState(defaultState),
      selectCategoryId: (id: number) => {
        setState({
          selectedCategoryId: id,
          // @ts-expect-error TS2322
          selectedProviderPriceListItemId: null,
          selectedProviderPriceListItemOptions: [],
          // @ts-expect-error TS2322
          selectedServiceProductId: null,
        })
      },
      selectPublicationEditionId: (id: number) => {
        const updatedEditionIds = toggleArray(selectedPublicationEditionIds, id)

        setState({
          selectedPublicationEditionIds: updatedEditionIds,
        })
      },
      selectLiftoverId: (id: number) => {
        const updatedLiftoverIds = toggleArray(selectedLiftoverIds, id)

        setState({
          selectedLiftoverIds: updatedLiftoverIds,
        })
      },
      selectProviderId: (id: number | undefined) => {
        setState({
          selectedProviderId: id,
        })
      },
      selectProviderPriceListItemOptionValue: (
        params: {
          providerPriceListItemOption: ProviderPriceListItemOptionModel,
          providerPriceListItemOptionValueId: number,
        },
      ) => {
        const { providerPriceListItemOption, providerPriceListItemOptionValueId } = params

        const selectPayload = selectPriceListItemOptionValue({
          providerPriceListItemOption,
          providerPriceListItemOptionValues,
          selectedProviderPriceListItemOptionValueId: providerPriceListItemOptionValueId,
          // @ts-expect-error TS2322
          selectedProviderPriceListItemOptions,
        })

        setState({
          selectedProviderPriceListItemOptions: selectPayload.selectedProviderPriceListItemOptions,
        })
      },
      selectProviderPriceListItemId: (id: number) => {
        const providerPriceListItem = providerPriceListItems[id]

        const autoSelectPayload = autoSelectProductOptions({
          providerPriceListItem,
          providerPriceListItemOptionValues,
          providerPriceListItemOptions,
        })

        setState({
          selectedProviderPriceListItemId: id,
          selectedProviderPriceListItemOptions: autoSelectPayload.selectedProviderPriceListItemOptions,
        })
      },
      selectProductSearchResult: (result: ProductResult) => {
        setState({
          selectedCategoryId: result.service_category_id,
          selectedProviderId: result.provider_id,
          selectedServiceId: result.service_id,
          selectedServiceProductId: result.product_id,
        })
      },
      selectPublicationId: (id: number) => {
        const publication = publications[id]

        setState({
          selectedCategoryId: undefined,
          selectedLiftoverIds: [],
          selectedProviderId: publication?.provider_id,
          selectedPublicationEditionIds: [],
          selectedPublicationId: id,
        })
      },
      selectServiceId: (id: number | string) => {
        const service = services[id]

        const autoSelectPayload = autoSelectProviderId({
          providerId,
          service,
        })

        setState({
          selectedProviderId: autoSelectPayload.providerId,
          // @ts-expect-error TS2322
          selectedServiceId: id,
        })
      },
      selectServiceProductId: (id: number) => {
        setState({
          selectedServiceProductId: id,
        })
      },
      selectTags: (tags: string[]) => {
        setState({
          selectedTags: tags,
        })
      },
      setPackageTitleFilter: (filter: string) => {
        setState({
          packageTitleFilter: filter,
        })
      },
      setState,
    },
    creating: creatingCampaignQuoteItem || creatingCampaignQuoteTemplateItem || creatingDefaultQuoteItem || creatingFavouriteOrder || creatingOrder,
    currentPrice,
    currentPriceList,
    filteredAgencyOrderBundles,
    filteredCategories,
    filteredFavouriteOrders,
    filteredProviderPackages,
    filteredProviderPriceListItems,
    filteredProviderPriceListProductUnlocks,
    filteredProviders,
    filteredProvidersWithPackages,
    filteredPublicationEditions,
    filteredPublications,
    filteredServiceProducts,
    filteredServices,
    hasFilteredCategories,
    isDefaultQuoteItem,
    isPublication,
    loadingAgencyPrice,
    loadingApiPricing,
    loadingPriceLists,
    loadingProviders,
    loadingPublicationEditions,
    loadingPublications,
    missingCampaignFields,
    selectedAgency,
    selectedCampaign,
    selectedCategory,
    selectedOptionsString,
    selectedPriceListItem,
    selectedProvider,
    selectedPublication,
    selectedPublicationEditionIds,
    selectedService,
    selectedServiceProduct,
    state: { ...state, selectedProviderPriceListItemOptions: selectedProviderPriceListItemOptions || [] },
    validOrder,
  }
}

export default useNewOrder
